import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaWifi, 
  FaExclamationTriangle, 
  FaTimes,
  FaSync,
  FaSignal
} from 'react-icons/fa';
import useNetworkStatus from '../../hooks/useNetworkStatus';

const ConnectionStatus = () => {
  const { isOnline, connectionQuality, isChecking, checkConnectionQuality } = useNetworkStatus();
  const [showTemporaryGoodStatus, setShowTemporaryGoodStatus] = useState(false);

  // Show a brief "Connection restored" message when connection improves
  useEffect(() => {
    if (isOnline && connectionQuality === 'good') {
      setShowTemporaryGoodStatus(true);
      const timer = setTimeout(() => {
        setShowTemporaryGoodStatus(false);
      }, 3000); // Show for 3 seconds

      return () => clearTimeout(timer);
    }
  }, [isOnline, connectionQuality]);

  // Don't show anything if connection is good and we're not showing the temporary status
  if (isOnline && connectionQuality === 'good' && !showTemporaryGoodStatus) {
    return null;
  }
  const getStatusConfig = () => {
    // Show connection restored message
    if (showTemporaryGoodStatus && isOnline && connectionQuality === 'good') {
      return {
        bgColor: 'bg-green-600',
        textColor: 'text-white',
        icon: <FaSignal className="text-white" />,
        message: 'Connection restored! You\'re back online.',
        showRetry: false,
        isTemporary: true
      };
    }

    if (!isOnline || connectionQuality === 'offline') {
      return {
        bgColor: 'bg-red-600',
        textColor: 'text-white',
        icon: <FaExclamationTriangle className="text-white" />,
        message: 'No internet connection. Please check your network.',
        showRetry: true,
        isTemporary: false
      };
    }

    if (connectionQuality === 'poor') {
      return {
        bgColor: 'bg-orange-500',
        textColor: 'text-white',
        icon: <FaWifi className="text-white" />,
        message: 'Poor internet connection. Some features may not work properly.',
        showRetry: true,
        isTemporary: false
      };
    }

    if (connectionQuality === 'fair') {
      return {
        bgColor: 'bg-yellow-500',
        textColor: 'text-white',
        icon: <FaWifi className="text-white" />,
        message: 'Slow internet connection detected.',
        showRetry: false,
        isTemporary: false
      };
    }

    return null;
  };

  const statusConfig = getStatusConfig();

  if (!statusConfig) return null;

  const handleRetry = () => {
    checkConnectionQuality();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-50 ${statusConfig.bgColor} ${statusConfig.textColor} py-2 px-4 shadow-lg`}
      >
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {isChecking ? (
                <FaSync className="animate-spin text-white" />
              ) : (
                statusConfig.icon
              )}
              <span className="text-sm font-medium">
                {isChecking ? 'Checking connection...' : statusConfig.message}
              </span>
            </div>
          </div>          <div className="flex items-center space-x-2">
            {statusConfig.showRetry && !isChecking && (
              <button
                onClick={handleRetry}
                className="text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded-full transition-all duration-200 flex items-center space-x-1"
              >
                <FaSync className="text-xs" />
                <span>Retry</span>
              </button>
            )}
            
            {/* Auto-checking indicator */}
            {!statusConfig.isTemporary && (
              <div className="text-xs opacity-75 flex items-center space-x-1">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>Live monitoring</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConnectionStatus;
